import { useState, useMemo } from 'react';
import {
  X,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  GitCompare,
  Clock,
  HardDrive,
  AlertCircle,
  Activity,
  BarChart3,
  Layers,
  ZoomIn,
  ZoomOut,
  Filter,
} from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

type FilterType =
  | 'all'
  | 'added'
  | 'removed'
  | 'slower'
  | 'faster'
  | 'larger'
  | 'smaller'
  | 'status-changed'
  | 'unchanged';

type ViewMode = 'list' | 'waterfall' | 'split';

interface ComparisonEntry {
  id: string;
  url: string;
  method: string;
  baseEntries: HAREntry[];
  compareEntries: HAREntry[];
  baseTime: number;
  compareTime: number;
  baseSize: number;
  compareSize: number;
  timeDiff: number;
  sizeDiff: number;
  timeDiffPercent: number;
  sizeDiffPercent: number;
  statusChanged: boolean;
  baseStatus?: number;
  compareStatus?: number;
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  baseStartTime?: number;
  compareStartTime?: number;
}

interface ComparisonStats {
  added: number;
  removed: number;
  slower: number;
  faster: number;
  larger: number;
  smaller: number;
  statusChanged: number;
  unchanged: number;
  total: number;
}

export function HARComparisonView() {
  const {
    har,
    fileName,
    compareHar,
    compareFileName,
    clearCompareHAR,
    selectEntry,
  } = useHARStore();

  const [filter, setFilter] = useState<FilterType>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [zoomLevel, setZoomLevel] = useState(1);

  // Build comparison entries with duplicate handling
  const comparisonEntries = useMemo((): ComparisonEntry[] => {
    if (!har || !compareHar) return [];

    const baseEntries = har.log.entries;
    const compareEntries = compareHar.log.entries;

    // Group entries by method+URL
    const baseGroups = new Map<string, HAREntry[]>();
    const compareGroups = new Map<string, HAREntry[]>();

    baseEntries.forEach((entry) => {
      const key = `${entry.request.method}:${entry.request.url}`;
      if (!baseGroups.has(key)) {
        baseGroups.set(key, []);
      }
      baseGroups.get(key)!.push(entry);
    });

    compareEntries.forEach((entry) => {
      const key = `${entry.request.method}:${entry.request.url}`;
      if (!compareGroups.has(key)) {
        compareGroups.set(key, []);
      }
      compareGroups.get(key)!.push(entry);
    });

    const allKeys = new Set([...baseGroups.keys(), ...compareGroups.keys()]);
    const entries: ComparisonEntry[] = [];

    allKeys.forEach((key, index) => {
      const baseItems = baseGroups.get(key) || [];
      const compareItems = compareGroups.get(key) || [];

      const baseTime = baseItems.reduce((sum, e) => sum + (e.time || 0), 0) / (baseItems.length || 1);
      const compareTime =
        compareItems.reduce((sum, e) => sum + (e.time || 0), 0) / (compareItems.length || 1);
      const baseSize = baseItems.reduce(
        (sum, e) => sum + (e.response.content.size || 0),
        0
      ) / (baseItems.length || 1);
      const compareSize =
        compareItems.reduce((sum, e) => sum + (e.response.content.size || 0), 0) /
        (compareItems.length || 1);

      const timeDiff = compareTime - baseTime;
      const sizeDiff = compareSize - baseSize;
      const timeDiffPercent = baseTime > 0 ? (timeDiff / baseTime) * 100 : 0;
      const sizeDiffPercent = baseSize > 0 ? (sizeDiff / baseSize) * 100 : 0;

      const baseStatus = baseItems[0]?.response.status;
      const compareStatus = compareItems[0]?.response.status;
      const statusChanged = baseItems.length > 0 && compareItems.length > 0 && baseStatus !== compareStatus;

      let type: ComparisonEntry['type'];
      if (baseItems.length === 0) type = 'added';
      else if (compareItems.length === 0) type = 'removed';
      else if (statusChanged || Math.abs(timeDiff) > 50 || Math.abs(sizeDiff) > 100) {
        type = 'modified';
      } else {
        type = 'unchanged';
      }

      entries.push({
        id: `entry-${index}`,
        url: key.split(':')[1],
        method: key.split(':')[0],
        baseEntries: baseItems,
        compareEntries: compareItems,
        baseTime,
        compareTime,
        baseSize,
        compareSize,
        timeDiff,
        sizeDiff,
        timeDiffPercent,
        sizeDiffPercent,
        statusChanged,
        baseStatus,
        compareStatus,
        type,
        baseStartTime: baseItems[0]?._startTime,
        compareStartTime: compareItems[0]?._startTime,
      });
    });

    return entries.sort((a, b) => {
      // Sort by type priority: removed, added, modified, unchanged
      const typePriority = { removed: 0, added: 1, modified: 2, unchanged: 3 };
      return typePriority[a.type] - typePriority[b.type];
    });
  }, [har, compareHar]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    let entries = comparisonEntries;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      entries = entries.filter(
        (entry) =>
          entry.url.toLowerCase().includes(query) ||
          entry.method.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    switch (filter) {
      case 'added':
        entries = entries.filter((e) => e.type === 'added');
        break;
      case 'removed':
        entries = entries.filter((e) => e.type === 'removed');
        break;
      case 'slower':
        entries = entries.filter((e) => e.timeDiff > 50);
        break;
      case 'faster':
        entries = entries.filter((e) => e.timeDiff < -50);
        break;
      case 'larger':
        entries = entries.filter((e) => e.sizeDiff > 100);
        break;
      case 'smaller':
        entries = entries.filter((e) => e.sizeDiff < -100);
        break;
      case 'status-changed':
        entries = entries.filter((e) => e.statusChanged);
        break;
      case 'unchanged':
        entries = entries.filter((e) => e.type === 'unchanged');
        break;
    }

    return entries;
  }, [comparisonEntries, filter, searchQuery]);

  // Calculate statistics
  const stats: ComparisonStats = useMemo(() => {
    const added = comparisonEntries.filter((e) => e.type === 'added').length;
    const removed = comparisonEntries.filter((e) => e.type === 'removed').length;
    const slower = comparisonEntries.filter((e) => e.timeDiff > 50).length;
    const faster = comparisonEntries.filter((e) => e.timeDiff < -50).length;
    const larger = comparisonEntries.filter((e) => e.sizeDiff > 100).length;
    const smaller = comparisonEntries.filter((e) => e.sizeDiff < -100).length;
    const statusChanged = comparisonEntries.filter((e) => e.statusChanged).length;
    const unchanged = comparisonEntries.filter((e) => e.type === 'unchanged').length;

    return {
      added,
      removed,
      slower,
      faster,
      larger,
      smaller,
      statusChanged,
      unchanged,
      total: comparisonEntries.length,
    };
  }, [comparisonEntries]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getChangeColor = (value: number, type: 'positive' | 'negative') => {
    if (type === 'positive') {
      return value > 0
        ? 'text-red-500'
        : value < 0
          ? 'text-green-500'
          : 'text-slate-400';
    }
    return value > 0
      ? 'text-green-500'
      : value < 0
        ? 'text-red-500'
        : 'text-slate-400';
  };

  const getChangeBgColor = (type: ComparisonEntry['type']) => {
    switch (type) {
      case 'added':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'removed':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'modified':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'unchanged':
        return 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700';
    }
  };

  const getTypeBadge = (type: ComparisonEntry['type']) => {
    switch (type) {
      case 'added':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400">
            <ArrowDown className="w-3 h-3" /> ADDED
          </span>
        );
      case 'removed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400">
            <ArrowUp className="w-3 h-3" /> REMOVED
          </span>
        );
      case 'modified':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400">
            <Activity className="w-3 h-3" /> MODIFIED
          </span>
        );
      case 'unchanged':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
            <Layers className="w-3 h-3" /> SAME
          </span>
        );
    }
  };

  // Waterfall timeline calculation
  const waterfallData = useMemo(() => {
    if (!har || !compareHar) return null;

    const baseStart = Math.min(
      ...har.log.entries.map((e) => new Date(e.startedDateTime).getTime())
    );
    const compareStart = Math.min(
      ...compareHar.log.entries.map((e) => new Date(e.startedDateTime).getTime())
    );
    const baseEnd = Math.max(
      ...har.log.entries.map((e) => new Date(e.startedDateTime).getTime() + (e.time || 0))
    );
    const compareEnd = Math.max(
      ...compareHar.log.entries.map((e) => new Date(e.startedDateTime).getTime() + (e.time || 0))
    );

    const maxDuration = Math.max(baseEnd - baseStart, compareEnd - compareStart);

    return {
      baseStart,
      compareStart,
      maxDuration,
    };
  }, [har, compareHar]);

  if (!compareHar) {
    return (
      <div className="card p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
            <GitCompare className="w-8 h-8 text-slate-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              No Comparison File Loaded
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-md mx-auto mb-4">
              Load a second HAR file to compare against <span className="font-medium text-slate-700 dark:text-slate-300">{fileName}</span>. 
              Click the <span className="font-medium text-brand-600 dark:text-brand-400">Compare</span> button in the header to get started.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 dark:bg-slate-800/50 px-4 py-2 rounded-lg">
            <span className="font-medium">Tip:</span> Look for the Compare button in the top navigation bar
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded p-1">
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'list'
                  ? 'bg-white dark:bg-slate-700 text-brand-600 dark:text-brand-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              <Layers className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('waterfall')}
              className={cn(
                'px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'waterfall'
                  ? 'bg-white dark:bg-slate-700 text-brand-600 dark:text-brand-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              <BarChart3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('split')}
              className={cn(
                'px-2 py-1 text-xs rounded transition-colors',
                viewMode === 'split'
                  ? 'bg-white dark:bg-slate-700 text-brand-600 dark:text-brand-400 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              <GitCompare className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={clearCompareHAR}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Close comparison"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* File names */}
      <div className="flex items-center gap-3 text-xs bg-slate-100 dark:bg-slate-800 p-3 rounded-lg">
        <div className="flex-1 min-w-0">
          <span className="text-[10px] text-slate-400 uppercase">Base</span>
          <div className="font-medium text-slate-700 dark:text-slate-300 truncate" title={fileName || ''}>
            {fileName}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-[10px] text-slate-400 uppercase">Compare</span>
          <div
            className="font-medium text-brand-600 dark:text-brand-400 truncate"
            title={compareFileName || ''}
          >
            {compareFileName}
          </div>
        </div>
      </div>

      {/* Stats summary cards */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
        <button
          onClick={() => setFilter(filter === 'added' ? 'all' : 'added')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'added'
              ? 'bg-green-100 dark:bg-green-900/40 border-green-300 dark:border-green-700'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-green-600 dark:text-green-400">{stats.added}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Added</div>
        </button>
        <button
          onClick={() => setFilter(filter === 'removed' ? 'all' : 'removed')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'removed'
              ? 'bg-red-100 dark:bg-red-900/40 border-red-300 dark:border-red-700'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-red-600 dark:text-red-400">{stats.removed}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Removed</div>
        </button>
        <button
          onClick={() => setFilter(filter === 'slower' ? 'all' : 'slower')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'slower'
              ? 'bg-orange-100 dark:bg-orange-900/40 border-orange-300 dark:border-orange-700'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.slower}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Slower</div>
        </button>
        <button
          onClick={() => setFilter(filter === 'faster' ? 'all' : 'faster')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'faster'
              ? 'bg-emerald-100 dark:bg-emerald-900/40 border-emerald-300 dark:border-emerald-700'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{stats.faster}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Faster</div>
        </button>
        <button
          onClick={() => setFilter(filter === 'larger' ? 'all' : 'larger')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'larger'
              ? 'bg-purple-100 dark:bg-purple-900/40 border-purple-300 dark:border-purple-700'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-purple-600 dark:text-purple-400">{stats.larger}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Larger</div>
        </button>
        <button
          onClick={() => setFilter(filter === 'smaller' ? 'all' : 'smaller')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'smaller'
              ? 'bg-cyan-100 dark:bg-cyan-900/40 border-cyan-300 dark:border-cyan-700'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-cyan-600 dark:text-cyan-400">{stats.smaller}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Smaller</div>
        </button>
        <button
          onClick={() => setFilter(filter === 'status-changed' ? 'all' : 'status-changed')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'status-changed'
              ? 'bg-yellow-100 dark:bg-yellow-900/40 border-yellow-300 dark:border-yellow-700'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-yellow-600 dark:text-yellow-400">
            {stats.statusChanged}
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Status</div>
        </button>
        <button
          onClick={() => setFilter(filter === 'unchanged' ? 'all' : 'unchanged')}
          className={cn(
            'p-2 rounded-lg text-center transition-all border',
            filter === 'unchanged'
              ? 'bg-slate-200 dark:bg-slate-700 border-slate-300 dark:border-slate-600'
              : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
          )}
        >
          <div className="text-lg font-bold text-slate-600 dark:text-slate-400">{stats.unchanged}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">Same</div>
        </button>
      </div>

      {/* Search and filter bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search URLs or methods..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-slate-200"
          />
        </div>
        {filter !== 'all' && (
          <button
            onClick={() => setFilter('all')}
            className="px-3 py-2 text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
          >
            Clear filter
          </button>
        )}
        {viewMode === 'waterfall' && (
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.25))}
              className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              title="Zoom out"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-500 px-1">{Math.round(zoomLevel * 100)}%</span>
            <button
              onClick={() => setZoomLevel((z) => Math.min(2, z + 0.25))}
              className="p-1 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
              title="Zoom in"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-xs text-slate-500 dark:text-slate-400">
        Showing {filteredEntries.length} of {stats.total} requests
      </div>

      {/* Content area */}
      <div className="max-h-[600px] overflow-auto">
        {viewMode === 'list' && (
          <div className="space-y-2">
            {filteredEntries.map((entry) => {
              const isExpanded = expandedIds.has(entry.id);

              return (
                <div
                  key={entry.id}
                  className={cn(
                    'border rounded-lg overflow-hidden transition-all',
                    getChangeBgColor(entry.type)
                  )}
                >
                  {/* Header row */}
                  <div
                    onClick={() => toggleExpand(entry.id)}
                    className="flex items-center gap-3 p-3 cursor-pointer hover:opacity-80"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}

                    <span
                      className={cn(
                        'px-2 py-0.5 rounded text-[10px] font-medium flex-shrink-0',
                        entry.method === 'GET' &&
                          'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
                        entry.method === 'POST' &&
                          'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
                        entry.method === 'PUT' &&
                          'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
                        entry.method === 'DELETE' &&
                          'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
                        entry.method === 'PATCH' &&
                          'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                      )}
                    >
                      {entry.method}
                    </span>

                    <span
                      className="text-sm text-slate-700 dark:text-slate-300 truncate flex-1 font-medium"
                      title={entry.url}
                    >
                      {getFileName(entry.url)}
                    </span>

                    {getTypeBadge(entry.type)}

                    {/* Quick stats */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {entry.type !== 'added' && entry.type !== 'removed' && (
                        <>
                          {Math.abs(entry.timeDiff) > 50 && (
                            <div
                              className={cn(
                                'flex items-center gap-1 text-xs',
                                getChangeColor(entry.timeDiff, 'positive')
                              )}
                              title={`Time: ${formatTime(entry.baseTime)} → ${formatTime(entry.compareTime)}`}
                            >
                              <Clock className="w-3 h-3" />
                              {entry.timeDiff > 0 ? '+' : ''}
                              {entry.timeDiffPercent.toFixed(1)}%
                            </div>
                          )}
                          {Math.abs(entry.sizeDiff) > 100 && (
                            <div
                              className={cn(
                                'flex items-center gap-1 text-xs',
                                getChangeColor(entry.sizeDiff, 'positive')
                              )}
                              title={`Size: ${formatBytes(entry.baseSize)} → ${formatBytes(
                                entry.compareSize
                              )}`}
                            >
                              <HardDrive className="w-3 h-3" />
                              {entry.sizeDiff > 0 ? '+' : ''}
                              {entry.sizeDiffPercent.toFixed(1)}%
                            </div>
                          )}
                          {entry.statusChanged && (
                            <div
                              className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400"
                              title="Status code changed"
                            >
                              <AlertCircle className="w-3 h-3" />
                              {entry.baseStatus} → {entry.compareStatus}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 dark:border-slate-700 p-3 space-y-3">
                      {/* Comparison details grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Base details */}
                        {entry.baseEntries.length > 0 && (
                          <div
                            className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
                            onClick={() => selectEntry(entry.baseEntries[0])}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] text-slate-400 uppercase font-medium">
                                Base ({fileName})
                              </span>
                              {entry.baseEntries.length > 1 && (
                                <span className="text-[10px] text-slate-400">
                                  {entry.baseEntries.length} requests
                                </span>
                              )}
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Status:</span>
                                <span
                                  className={cn(
                                    'font-medium',
                                    entry.baseStatus && entry.baseStatus >= 400
                                      ? 'text-red-500'
                                      : entry.baseStatus && entry.baseStatus >= 300
                                        ? 'text-yellow-500'
                                        : 'text-green-500'
                                  )}
                                >
                                  {entry.baseStatus}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Time:</span>
                                <span className="text-slate-700 dark:text-slate-300">
                                  {formatTime(entry.baseTime)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Size:</span>
                                <span className="text-slate-700 dark:text-slate-300">
                                  {formatBytes(entry.baseSize)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Compare details */}
                        {entry.compareEntries.length > 0 && (
                          <div
                            className="p-3 bg-brand-50 dark:bg-brand-900/20 rounded-lg border border-brand-200 dark:border-brand-800 cursor-pointer hover:border-brand-300 dark:hover:border-brand-700 transition-colors"
                            onClick={() => selectEntry(entry.compareEntries[0])}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-[10px] text-brand-600 dark:text-brand-400 uppercase font-medium">
                                Compare ({compareFileName})
                              </span>
                              {entry.compareEntries.length > 1 && (
                                <span className="text-[10px] text-brand-600 dark:text-brand-400">
                                  {entry.compareEntries.length} requests
                                </span>
                              )}
                            </div>
                            <div className="space-y-1 text-xs">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Status:</span>
                                <span
                                  className={cn(
                                    'font-medium',
                                    entry.compareStatus && entry.compareStatus >= 400
                                      ? 'text-red-500'
                                      : entry.compareStatus && entry.compareStatus >= 300
                                        ? 'text-yellow-500'
                                        : 'text-green-500'
                                  )}
                                >
                                  {entry.compareStatus}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Time:</span>
                                <span className="text-slate-700 dark:text-slate-300">
                                  {formatTime(entry.compareTime)}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Size:</span>
                                <span className="text-slate-700 dark:text-slate-300">
                                  {formatBytes(entry.compareSize)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Differences summary */}
                      {entry.type === 'modified' && (
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          <div
                            className={cn(
                              'p-2 rounded text-center',
                              entry.timeDiff > 0
                                ? 'bg-red-50 dark:bg-red-900/20'
                                : entry.timeDiff < 0
                                  ? 'bg-green-50 dark:bg-green-900/20'
                                  : 'bg-slate-50 dark:bg-slate-800'
                            )}
                          >
                            <div
                              className={cn(
                                'font-medium',
                                getChangeColor(entry.timeDiff, 'positive')
                              )}
                            >
                              {entry.timeDiff > 0 ? '+' : ''}
                              {formatTime(Math.abs(entry.timeDiff))}
                            </div>
                            <div className="text-[10px] text-slate-500">Time difference</div>
                          </div>
                          <div
                            className={cn(
                              'p-2 rounded text-center',
                              entry.sizeDiff > 0
                                ? 'bg-red-50 dark:bg-red-900/20'
                                : entry.sizeDiff < 0
                                  ? 'bg-green-50 dark:bg-green-900/20'
                                  : 'bg-slate-50 dark:bg-slate-800'
                            )}
                          >
                            <div
                              className={cn(
                                'font-medium',
                                getChangeColor(entry.sizeDiff, 'positive')
                              )}
                            >
                              {entry.sizeDiff > 0 ? '+' : ''}
                              {formatBytes(Math.abs(entry.sizeDiff))}
                            </div>
                            <div className="text-[10px] text-slate-500">Size difference</div>
                          </div>
                          <div className="p-2 rounded text-center bg-slate-50 dark:bg-slate-800">
                            <div className="font-medium text-slate-700 dark:text-slate-300">
                              {entry.baseEntries.length + entry.compareEntries.length}
                            </div>
                            <div className="text-[10px] text-slate-500">Total requests</div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {filteredEntries.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No requests match the selected filters</p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'waterfall' && waterfallData && (
          <div className="space-y-1">
            {filteredEntries.map((entry) => {
              const baseScale = entry.baseEntries[0]
                ? ((entry.baseTime / waterfallData.maxDuration) * 100 * zoomLevel)
                : 0;
              const compareScale = entry.compareEntries[0]
                ? ((entry.compareTime / waterfallData.maxDuration) * 100 * zoomLevel)
                : 0;

              return (
                <div
                  key={entry.id}
                  className={cn(
                    'flex items-center gap-2 py-1 px-2 text-xs border-b border-slate-100 dark:border-slate-800',
                    getChangeBgColor(entry.type)
                  )}
                >
                  <span className="w-12 flex-shrink-0 font-medium text-slate-600 dark:text-slate-400">
                    {entry.method}
                  </span>
                  <span className="w-48 truncate flex-shrink-0 text-slate-700 dark:text-slate-300">
                    {getFileName(entry.url)}
                  </span>

                  {/* Base timeline */}
                  <div className="flex-1 flex items-center gap-1 min-w-[100px]">
                    <span className="text-[10px] text-slate-400 w-8 text-right">Base</span>
                    <div className="flex-1 h-4 bg-slate-200 dark:bg-slate-700 rounded overflow-hidden">
                      {entry.baseEntries[0] && (
                        <div
                          className="h-full bg-slate-500 dark:bg-slate-400 rounded"
                          style={{ width: `${Math.min(baseScale, 100)}%` }}
                          title={`${formatTime(entry.baseTime)}`}
                        />
                      )}
                    </div>
                    <span className="text-[10px] text-slate-500 w-12 text-right">
                      {formatTime(entry.baseTime)}
                    </span>
                  </div>

                  {/* Compare timeline */}
                  <div className="flex-1 flex items-center gap-1 min-w-[100px]">
                    <span className="text-[10px] text-slate-400 w-8 text-right">New</span>
                    <div className="flex-1 h-4 bg-slate-200 dark:bg-slate-700 rounded overflow-hidden">
                      {entry.compareEntries[0] && (
                        <div
                          className={cn(
                            'h-full rounded',
                            entry.timeDiff > 50
                              ? 'bg-red-500'
                              : entry.timeDiff < -50
                                ? 'bg-green-500'
                                : 'bg-brand-500'
                          )}
                          style={{ width: `${Math.min(compareScale, 100)}%` }}
                          title={`${formatTime(entry.compareTime)}`}
                        />
                      )}
                    </div>
                    <span className="text-[10px] text-slate-500 w-12 text-right">
                      {formatTime(entry.compareTime)}
                    </span>
                  </div>
                </div>
              );
            })}

            {filteredEntries.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No requests match the selected filters</p>
              </div>
            )}
          </div>
        )}

        {viewMode === 'split' && (
          <div className="grid grid-cols-2 gap-4">
            {/* Base column */}
            <div className="space-y-2">
              <div className="sticky top-0 bg-slate-100 dark:bg-slate-800 p-2 rounded text-xs font-medium text-slate-600 dark:text-slate-400">
                {fileName} ({har?.log.entries.length || 0} requests)
              </div>
              <div className="space-y-1 max-h-[500px] overflow-auto">
                {filteredEntries
                  .filter((e) => e.baseEntries.length > 0)
                  .map((entry) => (
                    <div
                      key={`base-${entry.id}`}
                      className="p-2 bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 text-xs cursor-pointer hover:border-brand-300 dark:hover:border-brand-700"
                      onClick={() => selectEntry(entry.baseEntries[0])}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-medium">
                          {entry.method}
                        </span>
                        <span className="truncate text-slate-600 dark:text-slate-400">
                          {getFileName(entry.url)}
                        </span>
                      </div>
                      <div className="flex gap-3 text-[10px] text-slate-500">
                        <span>{formatTime(entry.baseTime)}</span>
                        <span>{formatBytes(entry.baseSize)}</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Compare column */}
            <div className="space-y-2">
              <div className="sticky top-0 bg-brand-50 dark:bg-brand-900/20 p-2 rounded text-xs font-medium text-brand-600 dark:text-brand-400">
                {compareFileName} ({compareHar?.log.entries.length || 0} requests)
              </div>
              <div className="space-y-1 max-h-[500px] overflow-auto">
                {filteredEntries
                  .filter((e) => e.compareEntries.length > 0)
                  .map((entry) => (
                    <div
                      key={`compare-${entry.id}`}
                      className={cn(
                        'p-2 rounded border text-xs cursor-pointer hover:border-brand-300 dark:hover:border-brand-700',
                        entry.type === 'added'
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                          : entry.type === 'modified'
                            ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                            : 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-800'
                      )}
                      onClick={() => selectEntry(entry.compareEntries[0])}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] font-medium">
                          {entry.method}
                        </span>
                        <span className="truncate text-slate-600 dark:text-slate-400">
                          {getFileName(entry.url)}
                        </span>
                      </div>
                      <div className="flex gap-3 text-[10px] text-slate-500">
                        <span
                          className={cn(
                            entry.timeDiff > 50 && 'text-red-500',
                            entry.timeDiff < -50 && 'text-green-500'
                          )}
                        >
                          {formatTime(entry.compareTime)}
                          {entry.timeDiff !== 0 && (
                            <span className="ml-1">
                              ({entry.timeDiff > 0 ? '+' : ''}
                              {entry.timeDiffPercent.toFixed(0)}%)
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            entry.sizeDiff > 100 && 'text-red-500',
                            entry.sizeDiff < -100 && 'text-green-500'
                          )}
                        >
                          {formatBytes(entry.compareSize)}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default HARComparisonView;
